import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type TicketRow = {
  id: number;
  store_id: number;
  public_id: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  updated_at: string;
  business_name?: string | null;
  slug?: string | null;
};

type MessageRow = {
  id: number;
  author_type: string;
  body: string;
  is_internal?: boolean;
  created_at: string;
};

export function MasterSupportInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('openish');
  const [selected, setSelected] = useState<{
    storeId: number;
    ticketId: number;
  } | null>(null);
  const [detail, setDetail] = useState<{
    ticket: TicketRow;
    messages: MessageRow[];
    tenant: { business_name: string; slug: string } | null;
  } | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const qs =
      statusFilter === 'openish'
        ? 'limit=80'
        : statusFilter === 'all'
          ? 'includeResolved=1&limit=80'
          : `status=${encodeURIComponent(statusFilter)}&limit=80`;
    const res = await apiFetch(`/api/v1/master/support/tickets?${qs}`);
    const body = (await res.json().catch(() => ({}))) as {
      available?: boolean;
      tickets?: TicketRow[];
      message?: string;
      error?: string;
    };
    if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
    setAvailable(body.available !== false);
    setTickets(Array.isArray(body.tickets) ? body.tickets : []);
  }, [statusFilter]);

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [load]);

  useEffect(() => {
    const storeId = Number(searchParams.get('storeId') || 0);
    const ticketId = Number(searchParams.get('ticketId') || 0);
    if (storeId > 0 && ticketId > 0) {
      void openDetail(storeId, ticketId);
    }
  }, [searchParams]);

  async function openDetail(storeId: number, ticketId: number) {
    setSelected({ storeId, ticketId });
    setDetail(null);
    setMessage(null);
    setSearchParams(
      { storeId: String(storeId), ticketId: String(ticketId) },
      { replace: true },
    );
    try {
      const res = await apiFetch(`/api/v1/master/support/tickets/${storeId}/${ticketId}`);
      const body = (await res.json().catch(() => ({}))) as {
        ticket?: TicketRow;
        messages?: MessageRow[];
        tenant?: { business_name: string; slug: string } | null;
        message?: string;
        error?: string;
      };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      if (!body.ticket) throw new Error('Ticket mancante');
      setDetail({
        ticket: body.ticket,
        messages: Array.isArray(body.messages) ? body.messages : [],
        tenant: body.tenant || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function patchStatus(status: string) {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/master/support/tickets/${selected.storeId}/${selected.ticketId}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ status }),
        },
      );
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setMessage(`Stato → ${status}`);
      await load();
      await openDetail(selected.storeId, selected.ticketId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/master/support/tickets/${selected.storeId}/${selected.ticketId}/messages`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ body: reply.trim(), is_internal: internal }),
        },
      );
      const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
      setReply('');
      setMessage(internal ? 'Nota interna salvata' : 'Risposta inviata');
      await load();
      await openDetail(selected.storeId, selected.ticketId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Supporto · Inbox tenant"
        subtitle={
          <>
            <Link to="/master">← Master</Link> ·{' '}
            <Link to="/master/support/sos">SOS emergenze</Link> · ticket autenticati cross-tenant
          </>
        }
      />

      {message ? <p className="sk-ok">{message}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}
      {!available ? (
        <p className="sk-mc-hint">Schema plugin_support non disponibile su questo DB.</p>
      ) : null}

      <SectionCard
        title={`${tickets.length} ticket`}
        actions={
          <label className="sk-field" style={{ margin: 0 }}>
            <span className="sk-mc-hint">Filtro</span>
            <select
              className="sk-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="openish">Aperti / pending</option>
              <option value="open">open</option>
              <option value="pending">pending</option>
              <option value="resolved">resolved</option>
              <option value="closed">closed</option>
              <option value="all">Tutti</option>
            </select>
          </label>
        }
      >
        <DataTable
          emptyMessage="Nessun ticket"
          rows={tickets.map((t) => ({ ...t, row_key: `${t.store_id}-${t.id}` }))}
          columns={[
            { key: 'public_id', label: 'ID' },
            {
              key: 'business_name',
              label: 'Tenant',
              render: (_v, row) =>
                `${row.business_name || '—'} (#${row.store_id})`,
            },
            { key: 'subject', label: 'Oggetto' },
            { key: 'status', label: 'Stato' },
            { key: 'priority', label: 'Priorità' },
            {
              key: 'updated_at',
              label: 'Aggiornato',
              render: (v) => (v ? new Date(String(v)).toLocaleString('it-IT') : '—'),
            },
            {
              key: 'actions',
              label: '',
              render: (_v, row) => (
                <button
                  type="button"
                  className="sk-btn sk-btn--quiet"
                  onClick={() => void openDetail(Number(row.store_id), Number(row.id))}
                >
                  Apri
                </button>
              ),
            },
          ]}
        />
      </SectionCard>

      {detail ? (
        <SectionCard
          title={`${detail.ticket.public_id} · ${detail.ticket.subject}`}
        >
          <p className="sk-mc-hint">
            {detail.tenant?.business_name || 'Tenant'} · store #{detail.ticket.store_id} ·{' '}
            <Link to={`/master/tenants?storeId=${detail.ticket.store_id}`}>Gestisci tenant</Link>
          </p>
          <p style={{ marginTop: '0.65rem', whiteSpace: 'pre-wrap' }}>{detail.ticket.body}</p>

          <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.85rem' }}>
            {['open', 'pending', 'resolved', 'closed'].map((st) => (
              <button
                key={st}
                type="button"
                className="sk-btn sk-btn--quiet"
                disabled={busy || detail.ticket.status === st}
                onClick={() => void patchStatus(st)}
              >
                → {st}
              </button>
            ))}
          </div>

          <h3 className="sk-mc-card__title" style={{ marginTop: '1.1rem', fontSize: '1.05rem' }}>
            Thread
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0', display: 'grid', gap: '0.55rem' }}>
            {detail.messages.map((m) => (
              <li key={m.id} className="sk-mc-hint" style={{ borderLeft: '3px solid #cbd5e1', paddingLeft: '0.65rem' }}>
                <strong>
                  {m.author_type}
                  {m.is_internal ? ' (interno)' : ''}
                </strong>{' '}
                · {new Date(m.created_at).toLocaleString('it-IT')}
                <div style={{ color: 'inherit', whiteSpace: 'pre-wrap', marginTop: '0.25rem' }}>
                  {m.body}
                </div>
              </li>
            ))}
          </ul>

          <label className="sk-field" style={{ marginTop: '1rem' }}>
            <span>Risposta Master</span>
            <textarea
              className="sk-input"
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
          </label>
          <label
            className="sk-field"
            style={{ flexDirection: 'row', gap: '0.4rem', alignItems: 'center', marginTop: '0.35rem' }}
          >
            <input
              type="checkbox"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
            />
            <span>Nota interna (non visibile al tenant)</span>
          </label>
          <button
            type="button"
            className="sk-btn"
            style={{ marginTop: '0.65rem' }}
            disabled={busy || !reply.trim()}
            onClick={() => void sendReply()}
          >
            Invia
          </button>
        </SectionCard>
      ) : null}
    </section>
  );
}
